/**
 * What the producer's crew list should say about one crew member.
 *
 * Before this existed the list only knew "is there a recent GPS fix?", so a
 * crew member who had switched location sharing off looked exactly like one
 * whose phone had died: "No signal", "Location not available". At Shutdown
 * Festival on 2026-08-08 that cost a producer most of a day chasing a fault
 * that was really a setting.
 *
 * Kept as a pure function so the rule is testable without a map, a query or a
 * clock, and so the badge, the detail line and any future surface cannot
 * disagree about what a state means. Shared by the producer web app's live
 * map and the PROduce Producer app's crew list (moved here in WP9); each
 * platform styles the badge itself (web: crewTrackingBadgeClass in
 * apps/web/src/lib/crewTrackingStatus.ts).
 */

import { isSharingWithProducer, type TrackingConsentSettings } from "./trackingConsent";

export type CrewTrackingStatus = "active" | "stationary" | "no-signal" | "tracking-off";

/**
 * How long without a fix before someone counts as stationary rather than
 * active.
 *
 * Deliberately not the same as the marker fade in `crewMarkerVisual`
 * (120s/600s/3600s): that expresses "how much do I trust this dot's position",
 * this expresses "is this person moving". A marker can be faded while the row
 * still reads Active. Same screen, two different questions — if you change
 * one, decide about the other rather than assume they should match.
 */
export const STATIONARY_AFTER_MS = 5 * 60 * 1000;

export interface CrewTrackingInput {
  /** Timestamp of the most recent accepted fix, or null when there is none. */
  lastFixAt: string | null | undefined;
  /**
   * The crew member's own settings, as `get_crew_tracking_consent` returns
   * them. `undefined` while the consent query is still loading, or for crew
   * who have not linked an account yet — in both cases we must NOT claim they
   * opted out.
   */
  consent: TrackingConsentSettings | undefined;
  now: number;
}

export function crewTrackingStatus(input: CrewTrackingInput): CrewTrackingStatus {
  const { lastFixAt, consent, now } = input;

  // Opting out wins over everything, including a stale fix from before the
  // switch was flipped: the honest answer to "where are they?" is "they are
  // not sharing", not a location they no longer consent to showing.
  //
  // `consent &&` matters: an absent row means "we do not know yet", which
  // isSharingWithProducer deliberately reads as sharing.
  if (consent && !isSharingWithProducer(consent)) {
    return "tracking-off";
  }

  if (!lastFixAt) return "no-signal";

  const age = now - new Date(lastFixAt).getTime();
  // An unparseable timestamp is missing data, not a fresh fix.
  if (Number.isNaN(age)) return "no-signal";

  return age > STATIONARY_AFTER_MS ? "stationary" : "active";
}

/**
 * How each status reads.
 *
 * One table rather than a switch per surface: the badge and the detail line
 * used to live in separate places, so a new status compiled cleanly and
 * silently rendered with no explanation. `Record` over the union means adding
 * a status now fails to compile until both are answered.
 */
const CREW_TRACKING_TEXT: Record<
  CrewTrackingStatus,
  { label: string; explanation: string | null }
> = {
  "tracking-off": {
    label: "Tracking off",
    explanation: "Location sharing is switched off on their phone",
  },
  "no-signal": {
    label: "No signal",
    explanation: "Location not available",
  },
  stationary: {
    label: "Stationary",
    explanation: null,
  },
  active: {
    label: "Active",
    explanation: null,
  },
};

/** Short badge text. */
export function crewTrackingLabel(status: CrewTrackingStatus): string {
  return CREW_TRACKING_TEXT[status].label;
}

/**
 * The line under the crew member when there is nothing to show on the map.
 * Null when there is a location to show instead.
 */
export function crewTrackingExplanation(status: CrewTrackingStatus): string | null {
  return CREW_TRACKING_TEXT[status].explanation;
}
